import { Component, OnDestroy, AfterViewInit, ChangeDetectorRef, inject, signal, computed, ElementRef, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { Subscription, firstValueFrom } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { QuickSearchService } from '../../services/quick-search.service';
import { LocalStorageStateService } from '../../services/local-storage-state.service';
import { QuickSearchResult, SearchResultType } from './quick-search-overlay.model';
import OrganizationService from '../../services/organization.service';
import { DepartmentService } from '../../services/department.service';
import { RouterLink } from '@angular/router';
import { DkCurrencyPipe } from '../../pipes/dk-currency';
import { TranslateSearchResultTypeEnum } from '../../pipes/translate-search-result-type-enum.pipe';
import { EnvironmentLoaderService } from '../../services/environment-loader.service';
import { populateQuickSearchGoToUrls } from '../../helpers/quick-search-url.helper';
import { RequiresFeatureRoleDirective } from '../../directives/requires-feature-role.directive';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatSuffix, MatPrefix, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

export class QuickSearchState {
  searchTerm: string | null = null;
}

@Component({
  selector: 'adm-quick-search-overlay',
  templateUrl: './quick-search-overlay.component.html',
  styleUrls: ['./quick-search-overlay.component.scss'],
  host: {
    '(window:keydown.escape)': 'handleEscapeKey($event)',
  },
  imports: [
    RouterLink,
    DkCurrencyPipe,
    TranslateSearchResultTypeEnum,
    ReactiveFormsModule,
    RequiresFeatureRoleDirective,
    MatIcon,
    MatTooltip,
    MatInput,
    MatFormField,
    MatButtonModule,
    MatSuffix,
    MatPrefix,
    MatLabel,
  ],
})
export class QuickSearchOverlayComponent implements AfterViewInit, OnDestroy {
  // Injected services (readonly for better immutability)
  private readonly quickSearchService = inject(QuickSearchService);
  private readonly localStorageStateService = inject(LocalStorageStateService);
  private readonly organizationService = inject(OrganizationService);
  private readonly departmentService = inject(DepartmentService);
  private readonly environmentLoaderService = inject(EnvironmentLoaderService);
  private readonly dialogRef = inject(MatDialogRef<QuickSearchOverlayComponent>);
  private readonly cdr = inject(ChangeDetectorRef);

  // Constants
  private readonly QUICK_SEARCH_KEY = 'quickSearchState';
  private readonly MAX_SEARCH_HISTORY = 10;
  private readonly SEARCH_DEBOUNCE_TIME = 300;

  // Form control with non-nullable typing
  queryControl: FormControl<string>;

  public queryElement = viewChild<ElementRef<HTMLInputElement>>('queryElement');

  // Signals for reactive state management
  isSearching = signal(false);
  quickSearchResults = signal<QuickSearchResult[]>([]);
  quickSearchQueryStrings = signal<string[]>([]);

  constructor() {
    // Initialize form control with stored value synchronously to prevent expression changed error
    const initialValue = this.getInitialQueryValue();
    this.queryControl = new FormControl(initialValue, { nonNullable: true });

    // Mark for check to ensure OnPush detects the initial state
    // Use detectChanges in ngAfterViewInit to ensure Material form field updates properly
    this.cdr.markForCheck();
  } // Computed properties
  searchResultHits = computed(() => this.quickSearchResults().reduce((sum, result) => sum + result.quickSearchResponses.length, 0));

  permissionMap = computed(() => ({
    [SearchResultType.Organization]: this.environmentLoaderService.virtualRoles['organization.read'],
    [SearchResultType.Department]: this.environmentLoaderService.virtualRoles['department.read'],
    [SearchResultType.ChargingStationLocation]: this.environmentLoaderService.virtualRoles['charging-station-location.read'],
    [SearchResultType.PricePolicy]: this.environmentLoaderService.virtualRoles['price-policy.read'],
    [SearchResultType.IdTag]: this.environmentLoaderService.virtualRoles['id-tag.read'],
    [SearchResultType.ChargingUser]: this.environmentLoaderService.virtualRoles['charging-user.read'],
  }));

  // Readonly constants
  readonly searchResultTypeEnum = SearchResultType;
  readonly organizationRouterLink = '/organizations';
  readonly departmentRouterLink = '/organizations';
  readonly okAppEmulatorRouterLink = '/ok-app-emulator';

  // Subscription management
  private subscription?: Subscription;

  ngAfterViewInit(): void {
    this.initializeFromStorage();
    this.setupSearchSubscription();

    this.dialogRef
      .afterOpened()
      .pipe(take(1))
      .subscribe(() => {
        this.focusSearchInput();
      });

    // Trigger change detection to ensure Material form field updates its state
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private getInitialQueryValue(): string {
    return this.localStorageStateService.getState<QuickSearchState>(this.QUICK_SEARCH_KEY)?.searchTerm ?? '';
  }

  private async initializeFromStorage(): Promise<void> {
    const restoredQuery = this.localStorageStateService.getState<QuickSearchState>(this.QUICK_SEARCH_KEY)?.searchTerm?.trim() ?? '';

    if (restoredQuery.length > 1) {
      await this.handleSearch(restoredQuery);
    }
  }

  private setupSearchSubscription(): void {
    this.subscription = this.queryControl.valueChanges
      .pipe(debounceTime(this.SEARCH_DEBOUNCE_TIME), distinctUntilChanged())
      .subscribe(async (searchTerm: string) => {
        await this.handleSearch(searchTerm.trim());
      });
  }

  private async handleSearch(searchTerm: string): Promise<void> {
    if (searchTerm.length > 1) {
      this.isSearching.set(true);
      this.updateSearchHistory(searchTerm);

      try {
        const encodedTerm = encodeURIComponent(searchTerm);
        const results = await firstValueFrom(this.quickSearchService.search(encodedTerm));

        if (results) {
          // Set results immediately so users see them even if URL enrichment fails
          this.quickSearchResults.set(results);

          try {
            await populateQuickSearchGoToUrls(results, this.organizationService, this.departmentService);
            this.quickSearchResults.set(results);
          } catch (enrichError) {
            console.warn('Failed to enrich search results with navigation URLs:', enrichError);
          }

          this.saveToStorage(searchTerm);
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        this.isSearching.set(false);
      }
    } else {
      this.clearResults();
    }
  }

  private updateSearchHistory(searchTerm: string): void {
    const currentHistory = this.quickSearchQueryStrings();
    const existingIndex = currentHistory.indexOf(searchTerm);

    let newHistory: string[];
    if (existingIndex === -1) {
      newHistory = [searchTerm, ...currentHistory.slice(0, this.MAX_SEARCH_HISTORY - 1)];
    } else {
      newHistory = [searchTerm, ...currentHistory.slice(0, existingIndex), ...currentHistory.slice(existingIndex + 1)];
    }

    this.quickSearchQueryStrings.set(newHistory);
  }

  private saveToStorage(query: string): void {
    this.localStorageStateService.setState<QuickSearchState>(this.QUICK_SEARCH_KEY, { searchTerm: query });
  }

  private clearResults(): void {
    this.quickSearchResults.set([]);
    this.saveToStorage('');
  }

  private focusSearchInput(): void {
    const inputElement = this.queryElement()?.nativeElement;
    if (!inputElement) {
      return;
    }

    inputElement.focus();

    if (document.activeElement !== inputElement) {
      setTimeout(() => {
        inputElement.focus();
      }, 50);
    }
  }

  // Public methods
  search(query: string): void {
    this.queryControl.setValue(query);
  }

  removeSuggestion(query: string): void {
    const currentHistory = this.quickSearchQueryStrings();
    const filteredHistory = currentHistory.filter(item => item !== query);
    this.quickSearchQueryStrings.set(filteredHistory);
    this.saveToStorage(this.queryControl.value);
  }

  closeSearchAndSearchResults(): void {
    this.dialogRef.close();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  resetQuery(): void {
    this.queryControl.setValue('');
  }

  handleEscapeKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.dialogRef.close();
  }
}
